import { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { SBHorizontal } from 'atoms/layout/flex';
import { HeadlineS } from 'atoms/typography';
import { NewPageHeader } from 'components/navigation/NewPageHeader';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { useToggle } from 'hooks/useToggle';
import { desktopAndAbove, tabletAndAbove } from 'style/mediaQuery';

import { LinkProps, SideBarLink as SidebarLink } from './SidebarLink';

export interface Link<ID extends string = string> extends LinkProps<ID> {
  id: ID;
}

interface Props<ID extends string = string> {
  activeLink?: ID;
  links: Link<ID>[];
  children: ReactNode;
  onClick?: (id: ID) => void;
  pageTitle?: MessageDescriptor;
  startWithOpenedMenu?: boolean;
}

const Root = styled.div`
  display: flex;
  @media ${tabletAndAbove} {
    min-height: calc(100vh - 72px);
    max-height: calc(100vh - 72px);
  }
`;

const Menu = styled.div`
  /* flex-shrink: 0; */
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--c-nd-100);
  min-width: 120px;

  @media ${desktopAndAbove} {
    min-width: 320px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  width: -webkit-fill-available;

  @media ${tabletAndAbove} {
    max-height: calc(100vh - 72px);
  }
`;

const DesktopContentHeader = styled(SBHorizontal)`
  width: 100%;
  width: -webkit-fill-available;
  padding: var(--triple-unit);
  border-bottom: 1px solid var(--c-nd-100);
  flex-wrap: wrap;
`;

const DesktopMain = styled.div`
  overflow-y: auto;
`;

const ContentTitle = styled(HeadlineS)`
  flex-shrink: 0;
  font: var(--t-headline-l);
  text-transform: none;
`;

const Toolbar = styled.div`
  display: inline-flex;
  gap: var(--unit);
  justify-content: flex-end;
`;

export const SidebarLayout = <ID extends string = string>({
  activeLink,
  links,
  children,
  onClick,
  pageTitle,
  startWithOpenedMenu = false,
}: Props<ID>) => {
  const [menuOpened, toggleMenuOpened] = useToggle(startWithOpenedMenu);
  const { up: isGreaterThanTablet } = useScreenSize('tablet');

  const toggleDialog = () => {
    toggleMenuOpened();
  };

  if (!activeLink && isGreaterThanTablet && !menuOpened) {
    toggleMenuOpened();
  }

  const activeLinkObj = links.find(link => link.id === activeLink);

  const menu = (
    <>
      {links.map(link => (
        <SidebarLink
          key={link.id}
          active={!!isGreaterThanTablet && activeLink === link.id}
          activeLink={(isGreaterThanTablet && activeLink) || undefined}
          onClick={() => {
            if (onClick) onClick(link.id);
            if (menuOpened) toggleMenuOpened();
          }}
          onClickNested={(id: ID) => {
            if (onClick) onClick(id);
          }}
          {...link}
          toggleMenuOpened={toggleDialog}
        />
      ))}
    </>
  );
  const contentHeaderTitle = activeLinkObj &&
    typeof activeLinkObj?.label !== 'string' && (
      <ContentTitle as="h5">
        <FormattedMessage {...activeLinkObj?.label} />
      </ContentTitle>
    );

  if (isGreaterThanTablet) {
    return (
      <Root>
        <NewPageHeader text={pageTitle} />
        <Menu>{menu}</Menu>
        <Content>
          <DesktopContentHeader>
            {contentHeaderTitle}
            <Toolbar id="sidebar-layout-toolbar" />
          </DesktopContentHeader>
          <DesktopMain>{children}</DesktopMain>
        </Content>
      </Root>
    );
  }

  return (
    <Root>
      <NewPageHeader
        text={menuOpened ? pageTitle : activeLinkObj?.label}
        onClick={!menuOpened ? toggleMenuOpened : undefined}
      />
      <Content>
        {menuOpened ? (
          menu
        ) : (
          <>
            {/* as the `sidebar-layout-toolbar` can be empty, don't display the bordered content header here */}
            {children}
          </>
        )}
      </Content>
    </Root>
  );
};
